import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ApiError, api } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import type { SalesConfig } from "../../lib/sales-types";
import { Button } from "../ui/Button";
import { Overlay } from "../ui/Overlay";
import { Select } from "../ui/Select";
import { cn } from "../ui/cn";

/**
 * Raise a call.
 *
 * Every choice on this form is a button or a click except the two that are
 * genuinely free text, and that is the design. This gets filled in between
 * calls, often with a handset in one hand -- a form of dropdowns is a form of
 * two-step interactions, and the second step is where people give up and put
 * it on a sticky note instead.
 *
 * The company is a free-text box with CRM matches offered underneath rather
 * than a picker, for the same reason: a lead is usually a name somebody heard
 * thirty seconds ago, and refusing to record it until an account exists is how
 * leads stop being recorded. Linking is offered, never required.
 *
 * ## One box, six ways in
 *
 * What goes in that box is not only a company name. The same search backs the
 * CRM's own, and it matches the domain, the trade account number, the VAT
 * number, a *person's* name, and the account's customer id in Team -- so a rep
 * types whatever they happen to know and the account comes back.
 *
 * That last pair is the reason this was widened rather than left alone. Team
 * holds accounts under a person's name, or under no name that made it across
 * to Pulse at all, and those were unreachable here by any spelling of the
 * company: the rep would raise an unlinked call, and an unlinked call never
 * reaches the account's history no matter what is written on it. The customer
 * id in the address bar of the Team page they already have open is the one
 * handle those accounts always have.
 *
 * A hit on a person links the person as well as the account, in one click,
 * because "it's Sarah at that place" is a single thought and making it two
 * clicks is how the contact ends up unrecorded. What the search cannot offer
 * is a contact with no account behind them -- it returns companies by design,
 * and the unfiled queue is a different question -- so a genuinely
 * accountless lead is still a name typed into the box, which is what the box
 * is for.
 *
 * Once an account *is* linked, its people appear as buttons. Picking one is
 * both "ask for them" and "here is the number", because on a real desk those
 * are one decision. The number box below stays regardless, for the lead whose
 * contact is not on file yet.
 *
 * There is no "what it is about" field. The group buttons already say what
 * kind of call this is, and anything more specific belongs in the notes -- a
 * second one-line summary sitting above a notes box is a field people leave
 * empty.
 */
export function NewCallDialog({
  onClose,
  config,
  currentUserId,
  isAdmin,
  /** Pre-linked when raised from a CRM company page. */
  company,
}: {
  onClose: () => void;
  config: SalesConfig | undefined;
  currentUserId: number;
  isAdmin: boolean;
  company?: { id: number; name: string };
}) {
  const navigate = useNavigate();
  const client = useQueryClient();

  const [companyName, setCompanyName] = useState(company?.name ?? "");
  const [companyId, setCompanyId] = useState<number | null>(company?.id ?? null);
  const [contactId, setContactId] = useState<number | null>(null);
  const [phone, setPhone] = useState("");
  const [details, setDetails] = useState("");
  const [groupId, setGroupId] = useState<number | null>(null);
  const [assignee, setAssignee] = useState<number | null>(null);

  // Escape closes, matching every other overlay in the app.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /*
   * Matches for what has been typed, offered but never imposed. Disabled once
   * an account is linked -- the box then holds a chosen name, and re-searching
   * it would offer to relink what is already linked.
   */
  const matches = useQuery({
    queryKey: queryKeys.crmSearch(companyName),
    queryFn: () => crmApi.search(companyName),
    enabled: companyName.trim().length >= 2 && companyId === null,
    staleTime: 30_000,
  });

  /* Only once an account is known: without one there is nobody to ask for. */
  const contacts = useQuery({
    queryKey: ["crm", "contacts", companyId],
    queryFn: () => crmApi.contacts(companyId!),
    enabled: companyId !== null,
    staleTime: 60_000,
  });

  /* Only a manager can hand a call to somebody else, so only a manager needs
     the list of who. */
  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    enabled: isAdmin,
    staleTime: 5 * 60_000,
  });

  const create = useMutation({
    mutationFn: () =>
      salesApi.create({
        companyName: companyName.trim(),
        companyId,
        contactId,
        phone: phone.trim() || null,
        details: details.trim() || null,
        groupId,
        assigneeUserId: assignee,
      }),
    onSuccess: (call) => {
      void client.invalidateQueries({ queryKey: queryKeys.salesRoot });
      onClose();
      navigate(`/sales/calls/${call.id}`);
    },
  });

  /*
   * The company record, for its switchboard.
   *
   * Keyed the same way the company page keys it, so opening this dialog from a
   * company costs nothing -- the record is already in the cache.
   */
  const account = useQuery({
    queryKey: queryKeys.crmCompany(companyId ?? 0),
    queryFn: () => crmApi.company(companyId!),
    enabled: companyId !== null,
    staleTime: 5 * 60_000,
  });

  const mainLine = account.data?.phone ?? null;

  const groups = (config?.groups ?? []).filter((group) => group.isActive);
  const people = contacts.data ?? [];

  return (
    /* Actions in the frame's footer rather than at the end of the form, so
       they stay on screen while the fields scroll -- see `Overlay`. */
    <Overlay
      title="New sales call"
      size="md"
      onClose={onClose}
      footer={
        <>
          {create.isError ? (
            <p className="mr-auto text-xs text-red-600">
              {create.error instanceof ApiError
                ? create.error.message
                : "Could not raise that call."}
            </p>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            /* The group is as required as the company name, so it blocks
               the button in the same way rather than failing on submit. */
            disabled={!companyName.trim() || groupId === null || create.isPending}
          >
            {create.isPending ? "Raising…" : "Raise call"}
          </Button>
        </>
      }
    >
      <form
        id={FORM_ID}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (companyName.trim()) create.mutate();
        }}
      >
        <Field
          label="Company"
          hint="a name, a customer ID, or the person answering"
        >
          <input
            autoFocus
            value={companyName}
            onChange={(event) => {
              setCompanyName(event.target.value);
              /* Typing over a linked name unlinks it, and takes the chosen
                 person with it -- their number belongs to the old account. */
              setCompanyId(null);
              setContactId(null);
              setPhone("");
            }}
            placeholder="Who are we ringing?"
            className={INPUT}
          />
        </Field>

        {/* Said once, quietly, because linking is what makes the closed call
            show up in the account's history and there is otherwise nothing on
            screen to say the link took. */}
        {companyId !== null ? (
          <p className="-mt-2 text-[11px] text-faint">
            Linked to the CRM account — the closed call will appear in their
            activity.
          </p>
        ) : null}

        {companyId === null && (matches.data?.results.length ?? 0) > 0 ? (
          <div className="rounded-md border border-line">
            <p className="border-b border-line px-2 py-1 text-[11px] text-faint">
              Link to an existing account?
            </p>
            <ul className="max-h-48 overflow-y-auto">
              {matches.data!.results.slice(0, 6).map((result) => (
                <li key={result.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setCompanyId(result.id);
                      setCompanyName(result.name);
                      /*
                        A hit on a person links the person too, and brings
                        their number with it -- exactly what clicking their
                        chip below would do, saved a step.

                        Cleared rather than left alone when the hit was on
                        the company itself: the box may already be carrying a
                        contact picked against a *different* account a
                        keystroke ago, and a call asking for somebody who
                        does not work there is worse than one asking for
                        nobody.
                      */
                      setContactId(result.matchedContact?.id ?? null);
                      setPhone(result.matchedContact?.phone ?? "");
                    }}
                    className="flex w-full flex-col gap-0.5 px-2 py-1.5 text-left hover:bg-surface-2"
                  >
                    <span className="flex items-baseline gap-2">
                      <span className="truncate text-xs text-content">
                        {result.name}
                      </span>
                      {/*
                        The customer id, on every row rather than only the
                        row it matched. It is what the rep can check against
                        the Team page they already have open, and on an
                        account Team holds under somebody's name it is the
                        only part of the row that will look familiar.
                      */}
                      {result.external ? (
                        <span
                          title={`${result.external.source} customer ${result.external.id}`}
                          className="shrink-0 rounded bg-surface-2 px-1 text-[10px] tabular-nums text-faint"
                        >
                          #{result.external.id}
                        </span>
                      ) : null}
                      <span className="truncate text-[11px] text-faint">
                        {result.domain ?? result.city ?? ""}
                      </span>
                    </span>

                    {/*
                      Why this account is in the list at all, when the thing
                      typed was a surname. Without it a search for "Hargreaves"
                      returns a company nobody can see the relevance of --
                      and here it doubles as the confirmation that clicking
                      will ask for that person by name.
                    */}
                    {result.matchedContact ? (
                      <span className="truncate text-[11px] text-muted">
                        ask for{" "}
                        <span className="text-content">
                          {result.matchedContact.name ??
                            result.matchedContact.email ??
                            result.matchedContact.phone ??
                            "a contact"}
                        </span>
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/*
          The honest answer to a search that found nothing, which used to be
          silence.

          It matters most for the case this box was widened for: somebody who
          has pasted a customer id off a Team page and sees no result needs to
          know the search ran and the account is not here, rather than wonder
          whether ids are searchable at all. It also says what happens next,
          because raising it unlinked is a perfectly good outcome -- it is
          what every genuinely new lead does.
        */}
        {companyId === null &&
        companyName.trim().length >= 2 &&
        matches.isSuccess &&
        matches.data.results.length === 0 ? (
          <p className="-mt-2 text-[11px] text-faint">
            No CRM account matches that — the call will be raised as an
            unlinked lead.
          </p>
        ) : null}

        {/*
          Who to ring.

          Buttons for the people already on the account, because picking one
          is both "ask for them" and "here is their number" -- on a real desk
          those are one decision, not two.

          The box underneath is always there rather than hidden behind an
          "another number" button. It is the fallback, but a fallback you have
          to discover is one people give up on, and the whole point of this
          form is that a lead gets recorded rather than written on a pad.
        */}
        {companyId !== null && (people.length > 0 || mainLine) ? (
          <Field label="Who to ring">
            <div className="flex flex-wrap gap-1.5">
              {/*
                The switchboard first, before any individual.

                It is the honest default for a lead: you ring reception and
                ask, rather than picking a name off a list and hoping they
                are the right person. Picking it clears the contact, because
                "ask for nobody in particular" is a real choice and leaving a
                name attached to the main number would put the wrong thing in
                the call's history.
              */}
              {mainLine ? (
                <Chip
                  on={contactId === null && phone === mainLine}
                  onClick={() => {
                    setContactId(null);
                    setPhone(mainLine);
                  }}
                >
                  Main line
                  <span className="ml-1 opacity-70">({mainLine})</span>
                </Chip>
              ) : null}

              {people.map((contact) => (
                <Chip
                  key={contact.id}
                  on={contactId === contact.id}
                  onClick={() => {
                    /* Clicking the chosen one again clears it, so somebody
                       who picked the wrong person is not stuck with them. */
                    const same = contactId === contact.id;
                    setContactId(same ? null : contact.id);
                    setPhone(same ? "" : (contact.phone ?? ""));
                  }}
                >
                  {/* "Jade (Commercial Director)". Their job, not their
                      number: choosing who to ask for is a question about the
                      person, and the number is what the choice *sets* -- it
                      appears in the box below the moment one is picked. */}
                  {contact.name ?? contact.email}
                  {contact.role ? (
                    <span className="ml-1 opacity-70">({contact.role})</span>
                  ) : null}
                </Chip>
              ))}
            </div>
          </Field>
        ) : null}

        <Field label="Number">
          <input
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="01234 567890"
            className={INPUT}
          />
          {companyId !== null && people.length === 0 && !contacts.isLoading ? (
            <span className="mt-1 block text-[11px] text-faint">
              Unassigned
            </span>
          ) : null}
        </Field>

        {/*
          Groups as buttons, and there is no priority field beside them any
          more: the desk's groups already *are* its tiers, and asking for both
          produced calls that were High priority inside the Low priority
          group. Whichever group is first in settings is what gets rung first.
        */}
        {/*
          Required, and the chips no longer deselect.

          The group decides where a call sits in the worklist, which overdue
          rule applies and which section counts it -- so an ungrouped call is
          one nobody sees and nothing chases. Clicking the chosen group used
          to clear it, which is how they got made: a second click meant to
          confirm quietly unset it.
        */}
        <Field label="Group" hint="Required — it decides where the call is worked">
          <div className="flex flex-wrap gap-1.5">
            {groups.map((group) => (
              <Chip
                key={group.id}
                on={groupId === group.id}
                onClick={() => setGroupId(group.id)}
              >
                {group.name}
              </Chip>
            ))}
          </div>

          {groups.length === 0 ? (
            <p className="mt-1 text-[11px] text-sla-breached">
              There are no active call groups. One has to be set up in
              settings before a call can be raised.
            </p>
          ) : null}
        </Field>

        <Field label="Notes">
          <textarea
            value={details}
            onChange={(event) => setDetails(event.target.value)}
            rows={3}
            placeholder="Anything the person ringing should know first"
            className={cn(INPUT, "resize-y")}
          />
        </Field>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-xs text-muted">
            <input
              type="checkbox"
              checked={assignee === currentUserId}
              onChange={(event) =>
                setAssignee(event.target.checked ? currentUserId : null)
              }
            />
            {/*
              The common case is "I said I'd ring them back", and leaving that
              call in the pool for somebody else to pick up is worse than
              useless -- they would ring a lead already promised to a
              colleague.
            */}
            Claim it for myself
          </label>

          {/*
            Handing it to somebody else is a manager's, matching reassignment
            on the call itself -- and the API refuses it for anybody else, so
            this is the second lock rather than the only one. A rep who wants
            a colleague to take it raises it into the pool.
          */}
          {isAdmin ? (
            <label className="flex flex-wrap items-center gap-2 text-xs text-muted">
              or give it to
              <Select
                value={
                  assignee === null || assignee === currentUserId
                    ? ""
                    : String(assignee)
                }
                onChange={(value) => setAssignee(value ? Number(value) : null)}
                label="Assign to"
                options={[
                  { value: "", label: "Unassigned" },
                  ...(users.data ?? [])
                    .filter((person) => person.id !== currentUserId)
                    .map((person) => ({
                      value: String(person.id),
                      label: person.name,
                    })),
                ]}
              />
            </label>
          ) : null}
        </div>

      </form>
    </Overlay>
  );
}

/** Ties the footer's submit button to the form it now sits outside of. */
const FORM_ID = "new-sales-call";

const INPUT =
  "w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content placeholder:text-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40";

/**
 * A toggle button, the same one the attempt form uses.
 *
 * `aria-pressed` rather than a radio, because several of these lists allow
 * clicking the chosen one again to clear it -- which is a toggle, not a
 * single-choice group, and announcing it as the latter would be a lie.
 */
export function Chip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        "h-7 rounded-md border px-2 text-xs transition-colors",
        on
          ? "border-brand-600 bg-brand-600 font-medium text-white"
          : "border-line bg-surface text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
      )}
    >
      {children}
    </button>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  /** A word about why the field matters, beside the label rather than under it. */
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-faint">
        {label}
        {hint ? (
          <span className="ml-1.5 normal-case tracking-normal text-faint">
            {hint}
          </span>
        ) : null}
      </span>
      {children}
    </label>
  );
}
